import React from 'react';
import {Link} from 'react-router-dom'

const SchoolManagementSystem = () => {
  return (
    <section id="school-management" className="py-16 bg-gradient-to-br from-green-600 to-blue-600">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            EDUSMART SCHOOL MANAGEMENT SYSTEM
          </h2>
          <p className="text-green-100 text-lg max-w-3xl mx-auto">
            Simplify, Automate & Elevate Learning
          </p>
        </div>

        {/* School Management Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <img 
                src="/assets/SchoolMgt.png" 
                alt="EduSmart School Management System" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute top-4 left-4 bg-gradient-to-r from-green-500 to-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                <span className="animate-pulse mr-2">🎓</span> EDUCATION TECHNOLOGY
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Transform your school's administration with EduSmart
              </h3>
              <p className="text-green-600 font-semibold mb-4 text-lg">
                The all-in-one digital solution for modern educational institutions across Africa
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                From student enrollment to exam analytics, <strong>EduSmart</strong> streamlines operations, reduces paperwork, 
                and keeps parents, teachers, and administrators seamlessly connected. 
                Designed specifically for African schools with M-PESA integration and multilingual support.
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Why Choose EduSmart?</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>End-to-End Automation</strong> - Manage admissions, attendance, timetables, exams, and fees in one platform.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Real-Time Parent Engagement</strong> - Send automated SMS/email alerts for fees, grades, and attendance.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Exam & Performance Analytics</strong> - Generate report cards and track student progress with smart insights.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Fee Management</strong> - Accept payments via M-PESA and automate receipts and reminders.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>User-Friendly & Secure</strong> - Role-based logins with encrypted data protection.</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Perfect For:</h4>
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-100">
                  <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Primary & Secondary Schools <em>(Digitize daily operations)</em></span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Boarding Schools <em>(Track hostel occupancy and health)</em></span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Multi-Campus Institutions <em>(Centralize data across branches)</em></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Key Features:</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-start">
                      <span className="text-yellow-500 mr-2">•</span>
                      <span>Biometric Attendance <em>(Fingerprint or RFID systems)</em></span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-500 mr-2">•</span>
                      <span>Library & Inventory Management <em>(Books and equipment tracking)</em></span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-500 mr-2">•</span>
                      <span>Customizable Reports <em>(Export academic or financial data)</em></span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-green-600 font-semibold mb-6 text-center text-lg flex justify-center items-center">
                <span className="mr-2">Empower your school with technology - adopt EduSmart today!</span>
                <span className="text-2xl">🎓</span>
              </p>

              <Link
                to="/contact"  // Make sure this matches your registration route
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <span>Upgrate Your School's Efficiency Now</span>
                <svg 
                  className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your School Management?
            </h3>
            <p className="text-green-100 mb-6 text-lg">
              Join educational institutions across Africa that are digitizing their operations with EduSmart
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                          <Link
                            to="/contact"
                            className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
                          >
                            Get A Demo Now
                          </Link>
                          <Link
                            to="/contact"
                            className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
                          >
                            Contact Us Now
                          </Link>
                        </div>
            <p className="text-yellow-200 mt-6 text-sm">
              Powered by Infinity Tech Africa - Innovating Education for Africa's Future.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SchoolManagementSystem;